import { createContext, useContext, useRef, useCallback, type ReactNode, type MutableRefObject } from "react";

interface NavigationGuardContextType {
  registerGuard: (guard: () => boolean) => () => void;
  shouldBlock: () => boolean;
  blockedHandlerRef: MutableRefObject<((targetUrl: string) => void) | null>;
}

const NavigationGuardContext = createContext<NavigationGuardContextType | null>(null);

export function NavigationGuardProvider({ children }: { children: ReactNode }) {
  const guardsRef = useRef<Set<() => boolean>>(new Set());
  const blockedHandlerRef = useRef<((targetUrl: string) => void) | null>(null);

  const registerGuard = useCallback((guard: () => boolean) => {
    guardsRef.current.add(guard);
    return () => { guardsRef.current.delete(guard); };
  }, []);

  const shouldBlock = useCallback(() => {
    for (const guard of guardsRef.current) {
      if (guard()) return true;
    }
    return false;
  }, []);

  return (
    <NavigationGuardContext.Provider value={{ registerGuard, shouldBlock, blockedHandlerRef }}>
      {children}
    </NavigationGuardContext.Provider>
  );
}

/** Use inside NavigationGuardProvider – throws if missing. */
export function useNavigationGuard() {
  const ctx = useContext(NavigationGuardContext);
  if (!ctx) throw new Error("useNavigationGuard must be used within NavigationGuardProvider");
  return ctx;
}

/** Safe version – returns null when outside provider. For NavLink etc. */
export function useNavigationGuardSafe() {
  return useContext(NavigationGuardContext);
}
